import {useService} from '@cindedi/react-application';
import {abort} from '@cindedi/request-utilities/abort';
import {H1} from '@cindedi/ui/components/Heading/H1';
import {isRecord} from '@cindedi/utilities/isRecord';
import {truncate} from '@cindedi/utilities/truncate';
import type {MetaFunction, LoaderFunction} from '@remix-run/node';
import {json} from '@remix-run/node';
import {Link, useLoaderData} from '@remix-run/react';
import {PublicLayout} from '~/domains/organization/components/PublicLayout';

interface MemberStreamPublic {
  researchStreamId: string;
  researchStreamName: string;
  researchStreamSlug: string;
  roleName: string;
}

interface AssociatedProduction {
  id: string;
  title: string;
  year: string;
  type: string;
  author: string;
}

interface AssociatedPublication {
  id: string;
  title: string;
  year: string;
  type: string;
  authors: string;
}

export interface Membros$SlugLoaderData {
  member: Organization.PublicMemberDto;
  researchStreams: MemberStreamPublic[];
  productions: AssociatedProduction[];
  publications: AssociatedPublication[];
}

export const meta: MetaFunction = function Membros$SlugMeta({data}) {
  return isRecord(data) && data.member
    ? [
        {title: `${data.member.name} | Cindedi`},
        {name: 'description', content: truncate(data.member.textDescription, 155)},
      ]
    : [{title: 'Membro - Cindedi'}];
};

export const loader: LoaderFunction = async function Membros$SlugLoader({context: {use}, params}) {
  const Member = use('Member');
  const ResearchStream = use('ResearchStream');
  const {slug} = params;

  if (!slug) return abort(404);

  const member = await Member.find({where: {slug}});

  if (member == null) return abort(404);

  const researchStreams = await ResearchStream.getStreamsForMember(member.id);
  const productions = await ResearchStream.getMemberProductions(member.id);
  const publications = await ResearchStream.getMemberPublications(member.id);

  return json({
    member,
    researchStreams: researchStreams.map((rs) => ({
      researchStreamId: rs.researchStreamId,
      researchStreamName: rs.researchStreamName,
      researchStreamSlug: rs.researchStreamSlug,
      roleName: rs.roleName,
    })),
    productions,
    publications,
  });
};

export default function Membros$SlugView() {
  const {member, researchStreams, productions, publications} =
    useLoaderData<Membros$SlugLoaderData>();
  useService('organization/titlebar').hide();

  return (
    <PublicLayout>
      <div className="flex gap-4 items-end mb-6 mt-2 lg:mt-0">
        <div
          aria-label={member.name}
          className="h-40 w-40 rounded bg-cover bg-no-repeat bg-center bg-shade-2"
          style={{backgroundImage: `url(${member.cover.url})`}}
        />
        <div className="flex flex-col w-full gap-4">
          <H1 size="md">{member.name}</H1>
          <div className="w-full flex gap-2 text-primary-8 text-sm items-center bg-primary-1 p-4 rounded">
            <span>{member.position.name}</span>•
            <Link to={member.curriculum} target="_blank">
              Curriculum vitae
            </Link>
          </div>
        </div>
      </div>
      <div dangerouslySetInnerHTML={{__html: member.description}} />

      {researchStreams.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-shade-12 mb-4">Linhas de Pesquisa</h2>
          <ul role="list" className="space-y-2">
            {researchStreams.map((rs) => (
              <li key={rs.researchStreamId} className="bg-shade-2 rounded p-3">
                <Link
                  to={`/linhas-de-pesquisa/${rs.researchStreamSlug}`}
                  className="flex justify-between items-center hover:text-primary-11"
                >
                  <span className="font-medium text-primary-12">
                    {rs.researchStreamName}
                  </span>
                  <span className="text-sm text-shade-10">{rs.roleName}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {productions.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-shade-12 mb-4">Trabalhos</h2>
          <ul role="list" className="space-y-2">
            {productions.map((prod) => (
              <li key={prod.id} className="bg-shade-2 rounded p-3">
                <div className="flex flex-col">
                  <div className="flex gap-2 text-shade-10 text-sm mb-1">
                    <span>{prod.type}</span>•<span>{prod.year}</span>
                  </div>
                  <span className="font-medium text-primary-12">{prod.title}</span>
                  <span className="text-sm text-shade-11">{prod.author}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {publications.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-shade-12 mb-4">Publicações</h2>
          <ul role="list" className="space-y-2">
            {publications.map((pub) => (
              <li key={pub.id} className="bg-shade-2 rounded p-3">
                <div className="flex flex-col">
                  <div className="flex gap-2 text-shade-10 text-sm mb-1">
                    <span>{pub.type}</span>•<span>{pub.year}</span>
                  </div>
                  <span className="font-medium text-primary-12">{pub.title}</span>
                  <span className="text-sm text-shade-11">{pub.authors}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </PublicLayout>
  );
}
